"use client"

/**
 * DB-driven prompt modifier options.
 *
 * Fetches the modifier taxonomy (race / age / bodyType / facialExpression /
 * eyeColor option labels + groups) from public.prompt_modifiers, seeded by
 * supabase/migrations/20260610230100_prompt_modifier_taxonomy.sql.
 *
 * Options whose ids carry LoRA links (prompt_modifier_assets) are resolved to
 * real Runware LoRAs by the generation edge function — the frontend only ever
 * deals in (modifier_key, option_label) pairs, identical to the hardcoded
 * PROMPT_MODIFIER_CARDS shape, so this hook can back the existing UI with
 * zero behavioural change. Falls back to the provided defaults while loading
 * or if the query fails. `previewUrl` feeds the Generate Studio picker tiles.
 */

import { useQuery } from "@tanstack/react-query"
import { createClient } from "@/shared/lib/supabase/client"
import {
  MODIFIER_KEYS,
  groupPromptModifierRows,
  loadPromptModifierRows,
  type DbModifierOptions,
} from "../lib/prompt-modifier-options"

export type { DbModifierOption, DbModifierOptions } from "../lib/prompt-modifier-options"

/** Reads active modifier rows (with preview_url) and groups them per modifier key. */
async function fetchPromptModifierOptions(): Promise<DbModifierOptions> {
  const supabase = createClient()
  const rows = await loadPromptModifierRows((columns) =>
    supabase
      .from("prompt_modifiers")
      .select(columns)
      .in("modifier_key", MODIFIER_KEYS)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
  )
  return groupPromptModifierRows(rows)
}

/**
 * Returns DB-backed options per modifier key. While loading (or on error)
 * `options` is undefined per key — callers should fall back to their static
 * defaults so the picker is never empty.
 */
export function usePromptModifierOptions() {
  const query = useQuery({
    queryKey: ["prompt-modifier-options"],
    queryFn: fetchPromptModifierOptions,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  })

  return {
    options: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}
